import { apiClient } from './apiClient';
import type { ApiResponse } from '@/types/api.types';

// ─── Scanner bridge via localhost ─────────────────────────────────────────────

const SCANNER_URLS = [
  'https://localhost:18623',
  'http://localhost:18624',
];

let resolvedBaseUrl: string | null = null;

interface ScanResult {
  status: 'ok';
  frontImage: string;
  backImage: string | null;
  deviceName: string;
  scannedAt: string;
}

interface ScannerErrorResult {
  status: 'error';
  code: string;
  message: string;
}

type ScannerResponse = ScanResult | ScannerErrorResult;

// ─── Extraction types ─────────────────────────────────────────────────────────

export interface IdExtractionResult {
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  birthDate: string | null;
  gender: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  idNumber: string | null;
  expirationDate: string | null;
  confidence: {
    overall: 'high' | 'medium' | 'low';
    notes: string | null;
  };
}

export interface ScanAndExtractResult {
  extraction: IdExtractionResult;
  frontImage: string;
  backImage: string | null;
  /** Base64 (no data: prefix) of the front rotated upright by the server, when available. */
  correctedFrontImage: string | null;
  correctedFrontFormat: string | null;
}

/** Raw shape returned by /leads/id-extract: the extraction fields plus the upright front. */
type IdExtractApiResponse = IdExtractionResult & {
  correctedFrontImage: string | null;
  correctedFrontFormat: string | null;
};

export interface ExtractIdResult {
  extraction: IdExtractionResult;
  correctedFrontImage: string | null;
  correctedFrontFormat: string | null;
}

function normalizeScanImages(frontImage: string, backImage?: string | null): Pick<ScanResult, 'frontImage' | 'backImage'> {
  const normalizedBackImage = backImage && backImage !== frontImage ? backImage : null;
  return {
    frontImage,
    backImage: normalizedBackImage,
  };
}

// ─── Localhost communication ──────────────────────────────────────────────────

async function getBaseUrl(): Promise<string> {
  if (resolvedBaseUrl) return resolvedBaseUrl;

  for (const url of SCANNER_URLS) {
    try {
      const resp = await fetch(`${url}/ping`);
      if (resp.ok) {
        resolvedBaseUrl = url;
        return url;
      }
    } catch {
      // try next
    }
  }
  throw new Error('Scanner bridge not reachable.');
}

async function scannerFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const base = await getBaseUrl();
  const headers: Record<string, string> = {};
  if (options?.body) headers['Content-Type'] = 'application/json';
  const resp = await fetch(`${base}${path}`, { ...options, headers });
  return resp.json() as Promise<T>;
}

// ─── Public API ───────────────────────────────────────────────────────────────

export async function pingScannerHost(): Promise<boolean> {
  try {
    const resp = await scannerFetch<{ status: string }>('/ping');
    return resp.status === 'ok';
  } catch {
    return false;
  }
}

export function isScannerExtensionDetected(): boolean {
  return false;
}

// ─── Desk tag presentation ────────────────────────────────────────────────────

/**
 * What the local bridge reports about the tap link it is presenting on a
 * reader pad, if it has one attached.
 *
 * `available: false` is the ordinary case, not an error: the desk-tag routes
 * are a hardware-gated addition to the bridge (plan KTD8) and most sites run
 * a passive NFC sticker with no bridge installed at all. Every failure mode —
 * bridge absent, older bridge without the routes, a 404 whose body is not
 * JSON — collapses into this one shape so callers never have to distinguish
 * them, and never see a rejected promise for hardware that was optional.
 */
export interface DeskTagStatus {
  /** Whether a bridge on this computer answered the desk-tag request. */
  available: boolean;
  /** Whether it is currently presenting a tap link. */
  presenting: boolean;
  /** The link it is presenting, when it is presenting one. */
  url: string | null;
}

const DESK_TAG_UNAVAILABLE: DeskTagStatus = {
  available: false,
  presenting: false,
  url: null,
};

/** Bridge response body for the /desk-tag routes. */
interface DeskTagResponse {
  status?: unknown;
  /**
   * The bridge's word for "showing the link on the pad right now". The UI
   * calls the same state `presenting`; the rename happens here so only one
   * side has to know both words.
   */
  emulating?: unknown;
  url?: unknown;
}

/**
 * Narrow a bridge response to a status, or to "not available".
 *
 * `scannerFetch` does not inspect `resp.ok`, so a bridge that does not
 * implement these routes hands back whatever its 404 body parsed to. Anything
 * that is not an object carrying `status: 'ok'` is therefore treated as an
 * absent capability rather than trusted.
 */
function readDeskTagStatus(value: unknown): DeskTagStatus {
  if (!value || typeof value !== 'object') return DESK_TAG_UNAVAILABLE;
  const body = value as DeskTagResponse;
  if (body.status !== 'ok') return DESK_TAG_UNAVAILABLE;
  const url = typeof body.url === 'string' && body.url ? body.url : null;
  return {
    available: true,
    presenting: body.emulating === true,
    url,
  };
}

async function deskTagRequest(options?: RequestInit): Promise<DeskTagStatus> {
  try {
    return readDeskTagStatus(await scannerFetch<unknown>('/desk-tag', options));
  } catch {
    // No bridge on this computer, or an older one: not an error condition.
    return DESK_TAG_UNAVAILABLE;
  }
}

/** What the reader pad on this computer is presenting, if there is one. */
export function getDeskTagStatus(): Promise<DeskTagStatus> {
  return deskTagRequest();
}

/** Ask the reader pad to present this desk's tap link. */
export function setDeskTag(url: string): Promise<DeskTagStatus> {
  return deskTagRequest({ method: 'POST', body: JSON.stringify({ url }) });
}

/** Ask the reader pad to stop presenting a tap link. */
export function clearDeskTag(): Promise<DeskTagStatus> {
  return deskTagRequest({ method: 'DELETE' });
}

/**
 * Ask the scanner bridge to scan the inserted ID.
 *
 * The bridge owns sides and DPI; callers do not supply them. The bridge
 * always requests the best available duplex color scan at 300 DPI and
 * returns whatever the connected scanner actually produced (one image for
 * simplex scanners, two for duplex).
 */
export async function requestScan(
  options: { deviceId?: string } = {},
): Promise<ScanResult> {
  const response = await scannerFetch<ScannerResponse>('/scan', {
    method: 'POST',
    body: JSON.stringify({
      deviceId: options.deviceId,
    }),
  });

  if (response.status === 'error') {
    throw new Error((response as ScannerErrorResult).message);
  }

  const result = response as ScanResult;
  return {
    ...result,
    ...normalizeScanImages(result.frontImage, result.backImage),
  };
}

export async function extractIdFields(
  frontImage: string,
  backImage?: string | null,
): Promise<ExtractIdResult> {
  const response = await apiClient.post<ApiResponse<IdExtractApiResponse>>(
    '/leads/id-extract',
    { frontImage, backImage: backImage ?? null },
  );
  const { correctedFrontImage, correctedFrontFormat, ...extraction } = response.data.data;
  return { extraction, correctedFrontImage, correctedFrontFormat };
}

export async function scanAndExtract(
  options: { deviceId?: string } = {},
): Promise<ScanAndExtractResult> {
  const scanResult = await requestScan(options);
  const { extraction, correctedFrontImage, correctedFrontFormat } =
    await extractIdFields(scanResult.frontImage, scanResult.backImage);
  return {
    extraction,
    frontImage: scanResult.frontImage,
    backImage: scanResult.backImage,
    correctedFrontImage,
    correctedFrontFormat,
  };
}

export async function extractFromUploadedImages(
  frontFile: File,
  backFile?: File | null,
): Promise<ScanAndExtractResult> {
  const frontImage = await fileToBase64(frontFile);
  const backImage = backFile ? await fileToBase64(backFile) : null;
  const { extraction, correctedFrontImage, correctedFrontFormat } =
    await extractIdFields(frontImage, backImage);
  return { extraction, frontImage, backImage, correctedFrontImage, correctedFrontFormat };
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(',')[1];
      if (!base64) {
        reject(new Error('Failed to read file as base64.'));
        return;
      }
      resolve(base64);
    };
    reader.onerror = () => reject(new Error('Failed to read file.'));
    reader.readAsDataURL(file);
  });
}
